/**
 * I2 · Public coach directory with specialty / language filters.
 * I5 · "Verified credentials" badge on vetted coaches.
 */

import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { api } from '../../lib/api.ts';
import { Card, EmptyState, Field, Input, Spinner, Tag } from '../../lib/ui.tsx';

export interface CoachCard {
  id: string;
  name: string;
  bio: string;
  specialties: string[];
  languages: string[];
  introVideoUrl: string | null;
  credentialBadge: boolean;
  stats: {
    activeStudents: number;
    reviewsLast90d: number;
    medianReviewTurnaroundHours: number | null;
    avgRating: number | null;
    ratingCount: number;
  };
}

export function Stars({ rating, count }: { rating: number | null; count: number }) {
  if (rating === null) return <span className="text-xs text-slate-400">No ratings yet</span>;
  const full = Math.round(rating);
  return (
    <span className="text-xs text-slate-600">
      <span aria-hidden className="text-amber-500">
        {'★'.repeat(full)}
        {'☆'.repeat(5 - full)}
      </span>{' '}
      {rating.toFixed(1)} ({count})
    </span>
  );
}

export default function DirectoryPage() {
  const [specialty, setSpecialty] = useState('');
  const [language, setLanguage] = useState('');

  const params = new URLSearchParams();
  if (specialty.trim()) params.set('specialty', specialty.trim());
  if (language.trim()) params.set('language', language.trim());
  const qs = params.toString();

  const { data: coaches, isLoading } = useQuery({
    queryKey: ['directory', qs],
    queryFn: () => api<CoachCard[]>(`/api/marketplace/coaches${qs ? `?${qs}` : ''}`),
  });

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold">Find your vocal coach</h1>
        <p className="mt-1 text-sm text-slate-500">
          Every profile shows verified activity from real coaching on Solfo — not marketing claims.
        </p>
      </header>

      {/* Filters */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Specialty">
          <Input placeholder="e.g. belting, jazz, classical" value={specialty} onChange={(e) => setSpecialty(e.target.value)} />
        </Field>
        <Field label="Language">
          <Input placeholder="e.g. English, Deutsch" value={language} onChange={(e) => setLanguage(e.target.value)} />
        </Field>
      </div>

      {isLoading ? (
        <Spinner />
      ) : !coaches || coaches.length === 0 ? (
        <EmptyState title="No coaches match those filters." hint="Try widening your search." />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {coaches.map((coach) => (
            <Link key={coach.id} to={`/coaches/${coach.id}`} className="block">
              <Card className="h-full transition-shadow hover:shadow-md">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h2 className="text-base font-semibold">{coach.name}</h2>
                    <Stars rating={coach.stats.avgRating} count={coach.stats.ratingCount} />
                  </div>
                </div>
                {coach.credentialBadge && (
                  <div className="mt-2">
                    <Tag tone="green">✓ verified credentials</Tag>
                  </div>
                )}
                {coach.bio && <p className="mt-2 line-clamp-2 text-sm text-slate-600">{coach.bio}</p>}
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {coach.specialties.slice(0, 4).map((s) => (
                    <Tag key={s} tone="brand">
                      {s}
                    </Tag>
                  ))}
                  {coach.languages.slice(0, 3).map((l) => (
                    <Tag key={l}>{l}</Tag>
                  ))}
                </div>
                <p className="mt-3 text-xs text-slate-500">
                  {coach.stats.activeStudents} active students
                  {coach.stats.medianReviewTurnaroundHours !== null &&
                    ` · reviews in ~${coach.stats.medianReviewTurnaroundHours}h`}
                </p>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
